/**
 * @fileoverview TypeScript type definitions for the transcript API integration.
 *
 * This file contains all the type interfaces used in the Send Transcript feature,
 * including request/response structures and form data models.
 * It provides type safety and documentation for the transcript-related functionality.
 *
 * @author Palash
 * @version 1.0.0
 */

//#region API Interfaces

/**
 * Enum for transcript API response status
 * Defines the possible status values returned by the transcript API
 */
export enum TranscriptStatus {
	/** Operation completed successfully */
	Success = "success",
	/** Operation failed */
	Fail = "fail",
}

/**
 * Interface for transcript content data
 * Contains the email addresses and note for the transcript
 */
export interface TranscriptContent {
	/** Primary recipient email address */
	toEmail: string;
	/** Additional recipients as comma-separated email addresses */
	shareWith: string;
	/** Optional note to include with the transcript email */
	note: string;
}

/**
 * Request payload interface for the transcript API
 * Defines the structure of data sent to the backend when requesting a transcript
 */
export interface TranscriptRequestPayload {
	/** Content object containing email addresses and note */
	Content: TranscriptContent;
	/** Company identifier from the store */
	CompanyId: number;
	/** Bot identifier from the store */
	BotId: number | string;
	/** User identifier from the store */
	UserId: string;
	/** Conversation identifier from the store */
	ConvId: string;
	/** URL to send the message, typically the EBM API endpoint */
	SendMessageUrl: string;
}

/**
 * Response interface from the transcript API
 * Defines the structure of the API response when sending transcripts
 */
export interface TranscriptApiResponse {
	/** Status of the request - case-insensitive (SUCCESS, Success, FAIL, Fail, etc.) */
	status: TranscriptStatus;
	/** Descriptive message about the request result */
	status_message: string;
}

/**
 * Custom type for the setTimeout return value
 * This is used to manage timeouts in the application
 */
export type TimeoutId = ReturnType<typeof setTimeout> | undefined;

//#endregion

//#region Service Parameters

/**
 * Base parameters interface for the sendTranscript method
 * Contains common properties for all transcript requests
 */
interface BaseTranscriptParams {
	/** Optional note to include with the transcript */
	note?: string;
}

/**
 * Parameters interface for sending transcript with primary email
 */
interface PrimaryEmailParams extends BaseTranscriptParams {
	/** Primary recipient email address */
	primaryEmail: string;
	/** Array of additional email addresses to share with (optional) */
	additionalEmails?: string[];
}

/**
 * Parameters interface for sending transcript with additional emails only
 */
interface AdditionalEmailsParams extends BaseTranscriptParams {
	/** Primary recipient email address (not required if additionalEmails is provided) */
	primaryEmail?: string;
	/** Array of additional email addresses to share with */
	additionalEmails: string[];
}

/**
 * Union type for the sendTranscript method parameters
 * Ensures at least one of primaryEmail or additionalEmails must be provided
 */
export type SendTranscriptParams = PrimaryEmailParams | AdditionalEmailsParams;

//#endregion

//#region Form Types

/**
 * Interface for transcript form validation errors
 * Contains error message fields for each form input and general errors
 */
export interface TranscriptFormErrors {
	/** Error message for email field validation */
	email: string;
	/** Error message for shareWith field validation */
	shareWith: string;
	/** Error message for note field validation */
	note: string;
	/** General form error messages */
	general: string;
}

/**
 * Interface for the transcript form data structure
 * Contains all form fields, loading state, and validation errors
 */
export interface TranscriptFormData {
	/** Primary email recipient */
	email: string;
	/** Additional email recipients (comma-separated) */
	shareWith: string;
	/** Optional note to include with transcript */
	note: string;
	/** Whether the form is currently submitting */
	isLoading: boolean;
	/** Validation error messages for form fields */
	errors: TranscriptFormErrors;
}

/**
 * Interface for transcript modal message state
 * Contains success and error messages for form submission
 */
export interface TranscriptMessageState {
	/** Success message to display after successful submission */
	successMessage: string;
	/** Error message to display after failed submission */
	errorMessage: string;
}

//#endregion

//#region Validation Types

/**
 * Result of transcript validation
 */
export interface ValidationResult {
	/** Whether the validation passed */
	isValid: boolean;
	errorKey?: string;
	/** Error response if validation failed */
	errorResponse: TranscriptApiResponse;
}

//#endregion
